import React, { ButtonHTMLAttributes, ElementType, FocusEvent, HTMLAttributes, MouseEvent, ReactNode, forwardRef } from "react";

import { twMerge } from "tailwind-merge";

export type PopoverValue = "auto" | "manual" | "hint" | true;
export type PopoverTargetAction = "hide" | "show" | "toggle";
export type PopoverPlacement = "left" | "right" | "up" | "down";
export type PopoverTriggerType = "click" | "hover";

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, "popover" | "title"> {
  body?: boolean;
  children?: ReactNode;
  id: string;
  placement?: PopoverPlacement;
  popover?: PopoverValue;
  showBackdrop?: boolean;
}

export interface PopoverHeaderProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  children?: ReactNode;
}

export interface PopoverBodyProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  children?: ReactNode;
}

export interface PopoverTriggerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "popoverTargetAction" | "type"> {
  action?: PopoverTargetAction;
  as?: ElementType;
  placement?: PopoverPlacement;
  target: string;
  trigger?: PopoverTriggerType;
}

const arrowClasses: Record<PopoverPlacement, string> = {
  left: "-right-[7px] top-1/2 -translate-y-1/2 border-b-0 border-l-0",
  right: "-left-[7px] top-1/2 -translate-y-1/2 border-r-0 border-t-0",
  up: "-bottom-[7px] left-1/2 -translate-x-1/2 border-l-0 border-t-0",
  down: "-top-[7px] left-1/2 -translate-x-1/2 border-b-0 border-r-0",
};

const popoverGap = 12;
const hoverCloseDelay = 100;
const hoverCloseTimers = new Map<string, number>();

type NativePopoverElement = HTMLElement & {
  hidePopover?: () => void;
  showPopover?: () => void;
};

const getPopoverAttribute = (popover: PopoverValue) => (popover === true ? "auto" : popover);

const getPopoverElement = (target: string) => document.getElementById(target) as NativePopoverElement | null;

const isPopoverOpen = (popoverElement: HTMLElement) => popoverElement.matches(":popover-open");

const clearHoverCloseTimer = (target: string) => {
  const timer = hoverCloseTimers.get(target);
  if (!timer) return;

  window.clearTimeout(timer);
  hoverCloseTimers.delete(target);
};

const hideHoverPopover = (target: string) => {
  clearHoverCloseTimer(target);
  const popoverElement = getPopoverElement(target);
  if (!popoverElement || !isPopoverOpen(popoverElement)) return;

  popoverElement.hidePopover?.();
};

const scheduleHideHoverPopover = (target: string) => {
  clearHoverCloseTimer(target);
  hoverCloseTimers.set(
    target,
    window.setTimeout(() => hideHoverPopover(target), hoverCloseDelay),
  );
};

const keepHoverPopoverOpen = (target: string) => {
  const popoverElement = getPopoverElement(target);
  if (!popoverElement || popoverElement.dataset.cobaltPopoverHoverBound === "true") return;

  popoverElement.dataset.cobaltPopoverHoverBound = "true";
  popoverElement.addEventListener("mouseenter", () => clearHoverCloseTimer(target));
  popoverElement.addEventListener("mouseleave", () => scheduleHideHoverPopover(target));
};

const positionPopover = (trigger: HTMLElement, target: string, placement?: PopoverPlacement) => {
  const popoverElement = getPopoverElement(target);
  if (!popoverElement) return;

  const resolvedPlacement = placement || (popoverElement.dataset.cobaltPopoverPlacement as PopoverPlacement | undefined) || "right";
  const triggerRect = trigger.getBoundingClientRect();
  let top = triggerRect.top + triggerRect.height / 2;
  let left = triggerRect.left + triggerRect.width / 2;
  let translateX = "-50%";
  let translateY = "-50%";

  if (resolvedPlacement === "left") {
    left = triggerRect.left - popoverGap;
    translateX = "-100%";
  } else if (resolvedPlacement === "right") {
    left = triggerRect.right + popoverGap;
    translateX = "0";
  } else if (resolvedPlacement === "up") {
    top = triggerRect.top - popoverGap;
    translateY = "-100%";
  } else {
    top = triggerRect.bottom + popoverGap;
    translateY = "0";
  }

  popoverElement.style.setProperty("--cobalt-popover-top", `${top}px`);
  popoverElement.style.setProperty("--cobalt-popover-left", `${left}px`);
  popoverElement.style.setProperty("--cobalt-popover-translate-x", translateX);
  popoverElement.style.setProperty("--cobalt-popover-translate-y", translateY);
  popoverElement.dataset.cobaltPopoverPlacement = resolvedPlacement;
};

const showHoverPopover = (trigger: HTMLElement, target: string, placement?: PopoverPlacement) => {
  clearHoverCloseTimer(target);
  positionPopover(trigger, target, placement);
  keepHoverPopoverOpen(target);

  const popoverElement = getPopoverElement(target);
  if (!popoverElement || isPopoverOpen(popoverElement)) return;

  popoverElement.showPopover?.();
};

export const PopoverHeader = forwardRef<HTMLElement, PopoverHeaderProps>(({ as: Component = "div", children, className, ...rest }, ref) => {
  const classes = twMerge("px-4 py-2 font-semibold text-base-content", className);

  return React.createElement(Component, { ref, className: classes, ...rest }, children);
});

PopoverHeader.displayName = "Popover.Header";

export const PopoverBody = forwardRef<HTMLElement, PopoverBodyProps>(({ as: Component = "div", children, className, ...rest }, ref) => {
  const classes = twMerge("p-4 leading-5 text-base-content", className);

  return React.createElement(Component, { ref, className: classes, ...rest }, children);
});

PopoverBody.displayName = "Popover.Body";

const PopoverRoot = forwardRef<HTMLDivElement, PopoverProps>(
  ({ body, children, className, placement = "right", popover = "auto", showBackdrop = true, ...rest }, ref) => {
    const popoverAttribute = getPopoverAttribute(popover);
    const classes = twMerge(
      "fixed bottom-auto left-[var(--cobalt-popover-left,50%)] right-auto top-[var(--cobalt-popover-top,50%)] m-0 h-fit w-fit max-w-xs translate-x-[var(--cobalt-popover-translate-x,-50%)] translate-y-[var(--cobalt-popover-translate-y,-50%)] overflow-visible rounded-box border border-solid border-base-300 bg-base-100 p-0 text-left text-base-content shadow-sm [&:not(:popover-open)]:hidden",
      showBackdrop ? "backdrop:bg-base-content/20" : "backdrop:bg-transparent",
      className,
    );

    return (
      <div ref={ref} className={classes} data-cobalt-popover-placement={placement} {...{ popover: popoverAttribute }} {...rest}>
        <div className={twMerge("absolute h-3 w-3 rotate-45 border border-solid border-base-300 bg-base-100", arrowClasses[placement])} />
        {body ? <PopoverBody>{children}</PopoverBody> : children}
      </div>
    );
  },
);

PopoverRoot.displayName = "Popover";

export const PopoverTrigger = forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  (
    {
      action = "toggle",
      as: Component = "button",
      children,
      className,
      onBlur,
      onClick,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      placement,
      target,
      trigger = "click",
      ...rest
    },
    ref,
  ) => {
    const classes = twMerge(
      Component === "button" && "si-btn si-btn-primary h-[unset] min-h-[unset] border-none px-4 py-1 text-sm font-normal outline-2 -outline-offset-2",
      className,
    );
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented && trigger === "click" && action !== "hide") {
        positionPopover(event.currentTarget, target, placement);
      }
    };
    const handleMouseEnter = (event: MouseEvent<HTMLButtonElement>) => {
      onMouseEnter?.(event);
      if (!event.defaultPrevented && trigger === "hover") {
        showHoverPopover(event.currentTarget, target, placement);
      }
    };
    const handleMouseLeave = (event: MouseEvent<HTMLButtonElement>) => {
      onMouseLeave?.(event);
      if (!event.defaultPrevented && trigger === "hover") {
        scheduleHideHoverPopover(target);
      }
    };
    const handleFocus = (event: FocusEvent<HTMLButtonElement>) => {
      onFocus?.(event);
      if (!event.defaultPrevented && trigger === "hover") {
        showHoverPopover(event.currentTarget, target, placement);
      }
    };
    const handleBlur = (event: FocusEvent<HTMLButtonElement>) => {
      onBlur?.(event);
      if (!event.defaultPrevented && trigger === "hover") {
        scheduleHideHoverPopover(target);
      }
    };
    const nativePopoverTargetProps = trigger === "click" ? { popovertarget: target, popovertargetaction: action } : {};
    const componentProps = {
      ref,
      className: classes,
      onBlur: handleBlur,
      onClick: handleClick,
      onFocus: handleFocus,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...(Component === "button" && { type: "button" }),
      ...nativePopoverTargetProps,
      ...rest,
    };

    return React.createElement(Component, componentProps, children);
  },
);

PopoverTrigger.displayName = "Popover.Trigger";

type PopoverComponent = React.ForwardRefExoticComponent<PopoverProps & React.RefAttributes<HTMLDivElement>> & {
  Body: typeof PopoverBody;
  Header: typeof PopoverHeader;
  Trigger: typeof PopoverTrigger;
};

export const Popover = Object.assign(PopoverRoot, {
  Body: PopoverBody,
  Header: PopoverHeader,
  Trigger: PopoverTrigger,
}) as PopoverComponent;

export default Popover;
